import { create } from 'zustand';
import { loadPrism, type RigEditorApi } from '../engine/prism.js';
import { readProjectFile, setOverlay } from '../engine/host.js';
import { api } from '../lib/api.js';
import { docIO } from '../lib/doc-io.js';
import {
  cloneRig,
  duration,
  localBind,
  normalizeWeights,
  setKey,
  type Influence,
  type RigDoc,
  type RigFrame,
  type RigGeometry,
  type RigTransform,
} from '../lib/rig-doc.js';
import { useProjectFiles } from './project-files.js';
import { additiveKey, sampleTrack, type KeyRef } from '../lib/rig/curves.js';
import { finalize } from '../lib/rig/weights.js';
import type { OnionGhost } from '../components/model/RigViewport.js';

export interface OnionSkin {
  enabled: boolean;
  /** Ghost frames before / after the playhead. */
  before: number;
  after: number;
  /** Frames between ghosts. */
  step: number;
}

export type RigMode = 'skeleton' | 'skin' | 'animate';
interface RigState {
  path: string | null;
  projectId: number | null;
  rig: RigDoc;
  saved: string;
  diskVersion: string | null;
  engine: RigEditorApi | null;
  geometry: RigGeometry | null;
  frame: RigFrame | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  valid: boolean;
  skinUnlocked: boolean;
  selected: string | null;
  node: string;
  mode: RigMode;
  clip: string;
  time: number;
  playing: boolean;
  loop: boolean;
  speed: number;
  fps: number;
  autoKey: boolean;
  pose: RigTransform | null;
  previewPose: boolean;
  /** Clip layer receiving keys (-1 = base tracks). */
  layer: number;
  /** Selected keys (dope sheet / graph editor) of the active layer. */
  keySelection: KeyRef[];
  /** Snap key times to frames. */
  snap: boolean;
  /** Show times as frames instead of seconds. */
  showFrames: boolean;
  animView: 'dopesheet' | 'graph';
  /** Joints posed since their last key (Key All Modified). */
  modified: Record<string, RigTransform>;
  onion: OnionSkin;
  ghosts: OnionGhost[];
  /** Joints whose skin weights are locked against weight tools / painting. */
  weightLocks: string[];
  setOnion(onion: Partial<OnionSkin>): void;
  keyModified(): void;
  past: RigDoc[];
  future: RigDoc[];
  open(path: string, projectId: number): Promise<void>;
  close(): void;
  commit(rig: RigDoc, history?: boolean): void;
  select(name: string | null): void;
  seek(time: number): void;
  editPose(pose: RigTransform): void;
  keyPose(all?: boolean): void;
  save(): Promise<void>;
  undo(): void;
  redo(): void;
  paint(mesh: number, vertices: { index: number; strength: number }[], amount: number): void;
  explicitWeights(node: string): Influence[][][];
}
let loadId = 0;
const empty = (): RigDoc => ({ joints: [], clips: [], rigidBindings: {}, weights: {} });
const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));
export const useRig = create<RigState>((set, get) => {
  function sample(time: number) {
    const s = get();
    if (!s.engine || !s.valid) return;
    try {
      const frame = JSON.parse(
        s.engine.sample(s.mode === 'animate' ? s.clip : '', time),
      ) as RigFrame;
      const modified = s.selected ? s.modified[s.selected] : undefined;
      set({
        frame,
        time,
        pose: modified ?? frame.joints.find((j) => j.name === s.selected)?.local ?? null,
        ghosts: onionGhosts(time),
      });
    } catch (e) {
      set({ error: errorText(e), playing: false });
    }
  }
  /** Onion-skin frames around `time` (engine samples are stateless). */
  function onionGhosts(time: number): OnionGhost[] {
    const s = get();
    if (!s.onion.enabled || s.mode !== 'animate' || !s.clip || !s.engine) return [];
    const ghosts: OnionGhost[] = [];
    const end = clipDuration();
    const add = (count: number, sign: number, color: [number, number, number]) => {
      for (let i = 1; i <= count; i++) {
        const t = time + (sign * i * Math.max(1, s.onion.step)) / s.fps;
        if (t < -1e-6 || t > end + 1e-6) break;
        const fade = 1 - (i - 1) / (count + 1);
        const f = JSON.parse(s.engine!.sample(s.clip, Math.max(0, t))) as RigFrame;
        ghosts.push({
          positions: f.positions,
          color: color.map((c) => c * fade + 0.06 * (1 - fade)) as [number, number, number],
        });
      }
    };
    add(s.onion.before, -1, [0.95, 0.45, 0.25]);
    add(s.onion.after, 1, [0.3, 0.85, 0.45]);
    return ghosts;
  }
  /** The value to key for `joint` on the active layer so the pose becomes `pose`. */
  function layerValue(joint: string, pose: RigTransform): RigTransform {
    const s = get();
    const layer = s.rig.clips?.find((c) => c.name === s.clip)?.layers?.[s.layer];
    if (!layer || layer.blend !== 'additive') return pose;
    const current = s.frame?.joints.find((j) => j.name === joint)?.local;
    if (!current) return pose;
    const track = layer.tracks.find((t) => t.joint === joint);
    return additiveKey(pose, current, track?.keys.length ? sampleTrack(track, s.time) : undefined);
  }
  /** `rig` with the given joint poses keyed at the playhead on the active layer. */
  function keyed(rig: RigDoc, poses: Record<string, RigTransform>): RigDoc {
    const s = get();
    let next = rig;
    for (const [joint, pose] of Object.entries(poses))
      next = setKey(next, s.clip, joint, s.time, layerValue(joint, pose), s.layer);
    return next;
  }
  return {
    path: null,
    projectId: null,
    rig: empty(),
    saved: '',
    diskVersion: null,
    engine: null,
    geometry: null,
    frame: null,
    loading: false,
    saving: false,
    error: null,
    valid: false,
    skinUnlocked: false,
    selected: null,
    node: '',
    mode: 'skeleton',
    clip: '',
    time: 0,
    playing: false,
    loop: true,
    speed: 1,
    fps: 30,
    autoKey: false,
    pose: null,
    previewPose: false,
    layer: -1,
    keySelection: [],
    snap: true,
    showFrames: true,
    animView: 'dopesheet',
    modified: {},
    onion: { enabled: false, before: 2, after: 2, step: 3 },
    ghosts: [],
    weightLocks: [],
    past: [],
    future: [],
    async open(path, projectId) {
      const id = ++loadId;
      const old = get().engine;
      set({
        loading: true,
        saving: false,
        error: null,
        playing: false,
        path,
        projectId,
        geometry: null,
        frame: null,
        engine: null,
        rig: empty(),
        past: [],
        future: [],
        selected: null,
        clip: '',
        time: 0,
        saved: '',
        diskVersion: null,
        valid: false,
        pose: null,
        previewPose: false,
        skinUnlocked: false,
        modified: {},
        keySelection: [],
        layer: -1,
        ghosts: [],
      });
      old?.free();
      let engine: RigEditorApi | null = null;
      try {
        const module = await loadPrism();
        if (!module?.RigEditor)
          throw new Error(
            'Rig editor requires the current Prism Engine Cloud engine build. Rebuild the engine and reload.',
          );
        engine = new module.RigEditor();
        const bytes = await readProjectFile(projectId, path);
        if (path.endsWith('.glb')) engine.loadGlb(bytes);
        else {
          const text = new TextDecoder().decode(bytes);
          const doc = JSON.parse(text) as { materials?: Record<string, Record<string, unknown>> };
          const images = new Set<string>();
          for (const m of Object.values(doc.materials ?? {}))
            for (const key of ['map', 'normalMap', 'metallicRoughnessMap', 'ormMap', 'emissiveMap'])
              if (typeof m[key] === 'string') images.add(m[key]);
          await Promise.all(
            [...images].map(async (image) =>
              engine!.addFile(image, await readProjectFile(projectId, image)),
            ),
          );
          engine.loadModel(text);
        }
        const sidecar = `${path}.rig.json`;
        const probe = await api.get<{ exists: boolean }>(
          `/projects/${projectId}/exists?path=${encodeURIComponent(sidecar)}`,
        );
        const original = JSON.parse(engine.geometry()) as RigGeometry;
        const rig = probe.exists
          ? (JSON.parse(await docIO().read(projectId, sidecar)) as RigDoc)
          : original.sourceSkinned
            ? (JSON.parse(engine.initialRig()) as RigDoc)
            : empty();
        if (id !== loadId) {
          engine.free();
          return;
        }
        const text = JSON.stringify(rig);
        const skinUnlocked = probe.exists && rig.replaceSkin === true;
        if (!original.sourceSkinned || skinUnlocked) engine.bind(text);
        const geometry = JSON.parse(engine.geometry()) as RigGeometry;
        const selected = rig.joints[0]?.name ?? null;
        set({
          engine,
          rig,
          saved: text,
          diskVersion: probe.exists ? text : null,
          geometry,
          frame: geometry.frame,
          selected,
          node: geometry.meshes[0]?.node ?? '',
          clip: rig.clips?.[0]?.name ?? geometry.clips[0]?.name ?? '',
          valid: true,
          loading: false,
          error: null,
          skinUnlocked,
        });
        sample(0);
      } catch (e) {
        engine?.free();
        if (id === loadId) set({ engine: null, loading: false, error: errorText(e), valid: false });
      }
    },
    close() {
      ++loadId;
      get().engine?.free();
      set({
        engine: null,
        path: null,
        projectId: null,
        geometry: null,
        frame: null,
        rig: empty(),
        saved: '',
        error: null,
        playing: false,
        valid: false,
        past: [],
        future: [],
      });
    },
    commit(rig, history = true) {
      const s = get();
      set({
        rig,
        playing: false,
        past: history ? [...s.past, cloneRig(s.rig)].slice(-80) : s.past,
        future: history ? [] : s.future,
        error: null,
      });
      if (!s.engine) return;
      try {
        s.engine.bind(JSON.stringify(rig));
        const geometry = JSON.parse(s.engine.geometry()) as RigGeometry;
        const selected = rig.joints.some((j) => j.name === s.selected)
          ? s.selected
          : (rig.joints[0]?.name ?? null);
        const clip = rig.clips?.some((c) => c.name === s.clip)
          ? s.clip
          : (rig.clips?.[0]?.name ?? '');
        set({
          geometry,
          valid: true,
          previewPose: false,
          selected,
          clip,
          node: geometry.meshes.some((m) => m.node === s.node)
            ? s.node
            : (geometry.meshes[0]?.node ?? ''),
        });
        sample(s.time);
      } catch (e) {
        set({ error: errorText(e), valid: false });
      }
    },
    select(selected) {
      set({ selected });
      sample(get().time);
    },
    seek(time) {
      try {
        // Unkeyed poses are dropped when the playhead moves (as in Blender).
        if (Object.keys(get().modified).length && Math.abs(get().time - time) > 1e-6) set({ modified: {} });
        const s = get();
        if (s.previewPose && (!s.geometry?.sourceSkinned || s.skinUnlocked)) {
          s.engine?.bind(JSON.stringify(s.rig));
          set({ previewPose: false });
        }
        sample(Math.max(0, time));
      } catch (e) {
        set({ error: errorText(e), playing: false });
      }
    },
    editPose(pose) {
      const s = get();
      set({ pose, playing: false });
      if (!s.selected || !s.clip) return;
      // A temporary rig gives accurate feedback without recording a key until
      // the user keys (or records with Auto key). Every joint posed since its
      // last key stays in the preview (Key All Modified keys them).
      if (s.autoKey) {
        const { [s.selected]: _done, ...rest } = s.modified;
        set({ modified: rest });
        get().commit(keyed(s.rig, { [s.selected]: pose }));
        return;
      }
      const modified = { ...s.modified, [s.selected]: pose };
      set({ modified });
      const candidate = keyed(s.rig, modified);
      try {
        s.engine?.bind(JSON.stringify(candidate));
        const frame = JSON.parse(s.engine!.sample(s.clip, s.time)) as RigFrame;
        set({ frame, pose, previewPose: true, error: null });
      } catch (e) {
        set({ error: errorText(e) });
      }
    },
    keyPose(all = false) {
      const s = get();
      if (!s.clip) return;
      const poses: Record<string, RigTransform> = {};
      for (const joint of s.rig.joints)
        if (all || joint.name === s.selected)
          poses[joint.name] =
            s.modified[joint.name] ??
            (joint.name === s.selected && s.pose ? s.pose : undefined) ??
            s.frame?.joints.find((j) => j.name === joint.name)?.local ??
            localBind(s.rig, joint);
      const modified = { ...s.modified };
      for (const name of Object.keys(poses)) delete modified[name];
      set({ modified });
      get().commit(keyed(s.rig, poses));
    },
    keyModified() {
      const s = get();
      if (!s.clip || !Object.keys(s.modified).length) return;
      const poses = s.modified;
      set({ modified: {} });
      get().commit(keyed(s.rig, poses));
    },
    setOnion(onion) {
      set({ onion: { ...get().onion, ...onion } });
      set({ ghosts: onionGhosts(get().time) });
    },
    async save() {
      const s = get();
      if (!s.path || !s.engine || !s.valid || !s.projectId || s.saving) return;
      if (s.geometry?.sourceSkinned && !s.skinUnlocked) {
        set({ error: 'Choose Edit existing rig before saving changes to this GLB’s rig.' });
        return;
      }
      const rig = cloneRig(s.rig),
        text = JSON.stringify(rig, null, 2) + '\n';
      set({ saving: true, error: null });
      try {
        s.engine.bind(JSON.stringify(rig));
        // Engine export is also the full validation used by the AI tool.
        s.engine.exportGlb();
        const sidecar = `${s.path}.rig.json`;
        const probe = await api.get<{ exists: boolean }>(
          `/projects/${s.projectId}/exists?path=${encodeURIComponent(sidecar)}`,
        );
        const disk = probe.exists
          ? JSON.stringify(JSON.parse(await docIO().read(s.projectId, sidecar)))
          : null;
        if (disk !== s.diskVersion)
          throw new Error(
            'This rig changed on disk. Export your work or reload before saving over the other changes.',
          );
        await docIO().write(s.projectId, `${s.path}.rig.json`, text);
        if (get().path === s.path && get().projectId === s.projectId) {
          set({ saved: JSON.stringify(rig), diskVersion: JSON.stringify(rig) });
          setOverlay(`${s.path}.rig.json`, text);
          useProjectFiles.getState().refreshSoon();
        }
      } catch (e) {
        if (get().path === s.path) set({ error: errorText(e) });
      } finally {
        if (get().path === s.path) set({ saving: false });
      }
    },
    undo() {
      const s = get(),
        rig = s.past.at(-1);
      if (!rig) return;
      set({ past: s.past.slice(0, -1), future: [...s.future, cloneRig(s.rig)] });
      get().commit(cloneRig(rig), false);
    },
    redo() {
      const s = get(),
        rig = s.future.at(-1);
      if (!rig) return;
      set({ future: s.future.slice(0, -1), past: [...s.past, cloneRig(s.rig)] });
      get().commit(cloneRig(rig), false);
    },
    explicitWeights(node) {
      const s = get();
      if (s.rig.weights?.[node]) return structuredClone(s.rig.weights[node]);
      const primitives: Influence[][][] = [];
      s.geometry?.meshes.forEach((mesh, mi) => {
        if (mesh.node !== node) return;
        primitives[mesh.primitive] = (s.frame?.positions[mi] ?? []).map((_, vi) =>
          normalizeWeights(
            (mesh.weights[vi] ?? [])
              .map((weight, slot) => ({
                weight,
                joint: s.rig.joints[mesh.joints[vi]?.[slot] ?? 0]?.name ?? '',
              }))
              .filter((w) => w.joint),
          ),
        );
      });
      return primitives;
    },
    paint(meshIndex, vertices, amount) {
      const s = get(),
        mesh = s.geometry?.meshes[meshIndex];
      if (!mesh || !s.selected || !vertices.length) return;
      const rig = cloneRig(s.rig),
        weights = get().explicitWeights(mesh.node),
        primitive = weights[mesh.primitive];
      if (!primitive) return;
      for (const v of vertices) {
        const current = primitive[v.index] ?? [],
          other = current.filter((w) => w.joint !== s.selected);
        const value = Math.max(
          0,
          Math.min(
            1,
            (current.find((w) => w.joint === s.selected)?.weight ?? 0) + amount * v.strength,
          ),
        );
        const sum = other.reduce((n, w) => n + w.weight, 0);
        const next = other.map((w) => ({
          joint: w.joint,
          weight: sum ? (w.weight / sum) * (1 - value) : 0,
        }));
        next.push({ joint: s.selected, weight: value });
        // Locked joints keep their weight; the painted joint keeps its value.
        const normalized = s.weightLocks.length
          ? finalize(current, next, { locked: new Set(s.weightLocks), pinned: new Set([s.selected]) })
          : normalizeWeights(next);
        primitive[v.index] = normalized.length ? normalized : current;
      }
      rig.weights ??= {};
      rig.weights[mesh.node] = weights;
      if (rig.rigidBindings) delete rig.rigidBindings[mesh.node];
      get().commit(rig, false);
    },
  };
});

export function clipDuration(): number {
  const s = useRig.getState();
  return (
    duration(s.rig.clips?.find((c) => c.name === s.clip)) ||
    s.geometry?.clips.find((c) => c.name === s.clip)?.duration ||
    1
  );
}
export function resetPose(): void {
  const s = useRig.getState(),
    joint = s.rig.joints.find((j) => j.name === s.selected);
  if (joint) s.editPose(localBind(s.rig, joint));
}
export function downloadRigGlb(): void {
  const s = useRig.getState();
  try {
    if (!s.engine) return;
    s.engine.bind(JSON.stringify(s.rig));
    const bytes = s.engine.exportGlb(),
      url = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: 'model/gltf-binary' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = (s.path?.split('/').at(-1) ?? 'model').replace(
      /(\.model\.json|\.glb)$/,
      '-rigged.glb',
    );
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    useRig.setState({ error: errorText(e) });
  }
}
